import _ from 'lodash';
import { Checkbox, Col, Divider, Popover, Row, Select, Spin } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import '../../styles/class/classesCalendar.css';
import SelectCampus from '../../components/SelectCampus';
import { setCurrentSemesterCode } from '../../store/auth.slice';
import { ISemester } from '../../models/semesters.model';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import Button from '../../components/Button';
import { getClasses } from '../../api/campusClasses.api';
import useNotification from '../../hooks/useNotification';
import { IClass } from '../../models/campusClasses.model';
import useUtil from '../../hooks/useUtil';
import { doGetSemesters } from '../../store/semester.slice';
import { useGetClassTeachersQuery, useGetClassTeachersScheduleQuery } from '../../api/class/calendar/classCalendar.api';
import styled from 'styled-components';
import SimpleHeader from '../../components/layout/SimpleHeader';
import TableEmpty from '../../components/common/TableEmpty';
import exportTitleTable from '../../lib/exportTitleTable';
import { getCommonCodeLabel } from '../../lib/store';
import moment from 'moment';
import { getReverseYears } from '../../lib/util';

type DAY_OF_THE_WEEK_TYPE = {
  [index: string]: string
}

export const DAY_OF_THE_WEEK : DAY_OF_THE_WEEK_TYPE = {
  MONDAY: "MO",
  TUESDAY: "TU",
  WEDNESDAY: "WE",
  THURSDAY: "TH",
  FRIDAY: "FR"
}

export interface iExcelDataByTime {
  time: string;
  MO: string;
  TU: string;
  WE: string;
  TH: string;
  FR: string;
  mergeColumn?: string[];
  mergeSize?: number;
}


export enum CALENDAR_TAB {
  TEACHER,
  TIME
}

export const CLASS_STUDENT_STATUS = {
  REGISTER: 'RE',
  COMPLETION: 'GR'
}

export const CLASS_STATUS = {
  OPEN: "OP"
}

const TeachersTbody = styled.tbody`
    & td:nth-child(n+4) {
        white-space: nowrap;
    }
`

export const StudentPopover = ({ students, studentsCount }: any) => {
  const [open, setOpen] = useState<boolean>(false);

  const onHandleStudentList = (students: any) => {
    return (
      <>
        {
          students
            .map((student: any) => {
            return <p style={{
              textAlign: 'center',
              padding: 1
            }}>{student.memberName} {getCommonCodeLabel('SY0006', student.gradeCode, 'ko')}</p>
          })
        }
        <Button
          className={'btn-basic gray mt-1'}
          onClick={() => setOpen(false)}
          text={`Close`} />
      </>
    )
  }

  return (
    <Popover
      open={open}
      content={onHandleStudentList(students)}
      autoAdjustOverflow={true}
      trigger="click"
      placement={"topLeft"}
      zIndex={1040}
      getPopupContainer={(triggerNode) => triggerNode}
    >
      <p className="tit"
         onClick={() => setOpen(true)}>
        <i className="mb"></i>
        <span>{studentsCount}명</span>
      </p>
    </Popover>
  )
}


const ClassCalendar = () => {
  const { t } = useTranslation();
  const { infoNotification } = useNotification();
  const dispatch = useDispatch();
  const { getChunkArray } = useUtil();
  const { Option } = Select;

  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const { currentCampus, currentSemesterCode } = useSelector((state: RootState) => state.auth);
  const { semesters } = useSelector(
    (state: RootState) => state.semester,
  );
  const [standard, setStandard] = useState<CALENDAR_TAB>(CALENDAR_TAB.TEACHER);

  //교육단계 체크
  const [checkedList, setCheckedList] = useState<any[]>([]);
  const educationStage = commonCodes.find(
    (item) => item.commonCode === 'PR0006',
  )?.detailCode;
  // SY0005 = 연도
  const SY0005 = commonCodes.find(item => item.commonCode === 'SY0005')?.detailCode;
  // PR0002 = 학기유형
  const PR0002 = commonCodes.find((item) => item.commonCode === 'PR0002')?.detailCode;
  const checkAll = educationStage?.length === checkedList.length;
  const indeterminate = checkedList.length > 0 && checkedList.length < educationStage!.length;

  //과정 체크
  const [classes, setClasses] = useState<any>({});
  const [classCheckedList, setClassCheckedList] = useState<any[]>([]);
  
  const [year, setYear] = useState<string>();
  const [semesterType, setSemesterType] = useState("RG");
  const [semesterCode, setSemesterCode] = useState<number>();
  const [semesterData, setSemesterData] = useState<ISemester[]>([]);

  const filteredClasses = useMemo(() => {
    return Object.keys(classes)?.reduce((acc: Record<string, any>, key: any) => {
      const filteredChunks = classes[key].filter((chunk: any) => {
        const filterChunk = chunk.filter((_class: IClass) => checkedList?.includes(_class.eduStepCode));
        return filterChunk.length > 0;
      })

      if (filteredChunks.length > 0) {
        acc[key] = filteredChunks;
      }

      return acc;
    }, {});
  }, [classes, checkedList])

  const totalClassesCount = useMemo(
    () =>
      Object.keys(filteredClasses).reduce(
        (acc, key) => acc + filteredClasses[key].flat().length,
        0
      ),
    [filteredClasses]
  );

  const classCheckAll = totalClassesCount === classCheckedList.length;
  const classesIndeterminate = classCheckedList.length > 0 && classCheckedList.length < totalClassesCount;

  //강사 체크
  const { data: teachersResponse, refetch: teachersRefetch } = useGetClassTeachersQuery(classCheckedList);
  const [teacherCheckedList, setTeacherCheckedList] = useState<any[]>([]);

  const teacherCheckAll = useMemo(() => teachersResponse?.data?.length === teacherCheckedList.length, [teachersResponse, teacherCheckedList])
  const teacherIndeterminate = useMemo(() => teacherCheckedList.length > 0 && teacherCheckedList.length < teachersResponse?.data!.length, [teachersResponse, teacherCheckedList]);

  //시간표 가져오는 API
  const { data: teachersSchedule, isFetching, isLoading, refetch } = useGetClassTeachersScheduleQuery({
    standard: CALENDAR_TAB.TEACHER === standard ? 'teacher' : 'time',
    teachers: teacherCheckedList.map((memberCode: string) => {
      return { memberCode, classCodes: classCheckedList };
    }),
  })

  //시간표 시간으로 시간 필터 SelectBox
  const [timeOptions, setTimeOptions] = useState<string[]>([]);
  const [timeValue, setTimeValue] = useState<string>('');

  const schedules = useMemo(() => {
    if (standard === CALENDAR_TAB.TIME && timeValue && teachersSchedule?.data) {
       return Object.keys(teachersSchedule?.data)
          .filter(key => key === timeValue)
          .reduce((obj: any, key) => {
            obj[key] = teachersSchedule?.data[key];
            return obj;
          }, {});
    }

    return teachersSchedule?.data ?? {};
  }, [standard, timeValue, teachersSchedule]);

  const isSchedulesEmpty = useMemo(() => {
    if (!schedules) return true;

    return Array.isArray(schedules)
      ? schedules.length === 0
      : Object.keys(schedules).length === 0;
  }, [schedules])

  useEffect(() => {
    if (!teachersSchedule?.data || standard === CALENDAR_TAB.TEACHER) return;

    const times = Object.keys(teachersSchedule?.data)?.sort();
    setTimeOptions(times)

  }, [teachersSchedule?.data]);

  useEffect(() => {
    const flatClasses = Object.keys(filteredClasses).flatMap(key => filteredClasses[key].flat().map((classes: any) => classes.classCode));
    setClassCheckedList(classCheckedList.filter((classCode: string) => flatClasses.find((filterClass: string) => classCode === filterClass)));
  }, [checkedList]);

  useEffect(() => {
    if (!teachersResponse?.data) return;

    setTeacherCheckedList(teacherCheckedList.filter((teacherCode: string) => teachersResponse?.data?.find((teacher: any) => teacherCode === teacher.memberCode)));
  }, [teachersResponse]);

  //특강반 학기 조건 추가
  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
    setSemesterCode(undefined);
  }, [year]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
    setSemesterCode(undefined);
  }, [semesterType]);

  const onTeacherCheckChange = (e:any) => {
    setTeacherCheckedList(e);
  }
  const onTeacherCheckAllChange = (e: any) => {
    setTeacherCheckedList(e.target.checked ? teachersResponse?.data?.map((item: any) => item.memberCode) ?? [] : []);
  }

  const onCheckChange = async (e:any) => {
    setCheckedList(e);
  }

  const onClassCheckChange = (e: any) => {
    teachersRefetch();
    setClassCheckedList(e);
  }

  const onCheckAllChange = (e: any) => {
    const isChecked = e.target.checked;

    setCheckedList(isChecked ? educationStage?.map(item => item.detailCode) ?? [] : []);
  };

  const onClassCheckAllChange = useCallback((e: any) => {
    const isChecked = e.target.checked;
    let checkedArr: string[] = [];
    if (isChecked) {
      Object.keys(filteredClasses).forEach((key) => {
        filteredClasses[key].forEach((chunk: any)=> chunk?.map((_class: IClass) => checkedArr.push(_class?.classCode)))
      })
    }
    setClassCheckedList( isChecked ? checkedArr : []);
  }, [filteredClasses]);

  const onTabClick = (e: any) => {
    const tabIndex = e.target.tabIndex;
    if (tabIndex === -1) return;
    if (tabIndex === standard) return;

    refetch();
    setStandard(tabIndex);
  }

  const fnSetSemesterData = (year : string, semesterType : string) => {
    if(semesters) {
      let data: ISemester[] = _.cloneDeep(semesters);

      if(year) {
        data = data.filter((item: any) => item.learningYearCode === year);
      }

      if(semesterType) {
        data = data.filter((item: any) => item.semesterType === semesterType);
      }

      if(data.length === 0) {
        setSemesterCode(undefined);
      }

      setSemesterData(data);
    }
  }

  const callClassesAPI = async () => {
    if (!semesterCode) {
      return infoNotification(t('statistics.dttt.message1'));
    }

    return getClasses(currentCampus, semesterCode);
  }

  const classesProcess = async () => {
    const res = await callClassesAPI();
    const _response = res?.data?.data;
    const _classes = _response?.filter((_class: any) => _class.classStatus === CLASS_STATUS.OPEN)
      .sort((a: any, b: any) => a.semesterCourseCode - b.semesterCourseCode)
      .reduce((acc: any, curr: any) => {

        const { levelName } = curr;

        if (acc[levelName]) {
          acc[levelName].push(curr);
        } else {
          acc[levelName] = [curr];
        }

        return acc;
      }, {});

    let _ret: any = {};

    Object.keys(_classes)
      .forEach(key => {
        _ret[key] = getChunkArray(_classes[key], 3)
      });

    setClasses(_ret)
  }

  const search = async () => {
    if (!semesterCode) {
      return infoNotification(t('statistics.dttt.message1'));
    }

    await classesProcess();

    setCheckedList([]);
    setClassCheckedList([]);
    setTeacherCheckedList([]);
  }

  const isTeacherNoSchedule = (currentSchedule:any, week: string) => {
    const _schedules = schedules?.filter((schedule:any) => schedule.member.memberCode === currentSchedule.member.memberCode);
    return _schedules?.every((schedule: any) => {
      const _isEmpty = schedule.classes.some((_class: any) => {
        return _class.classWeekdays?.find((_week: any) => week === _week.lessonWeekday);
      })
      return !!_isEmpty;
    });
  }

  const calculateFilteredStudents = useCallback((classStudents: any[]) => {
    return classStudents?.filter(student =>
      Object.values(CLASS_STUDENT_STATUS).includes(student.classStudentStatusCode)
    ) || [];
  }, []);

  const handleExcelDownload = useCallback(() => {
    if (!schedules || schedules.length === 0) {
      infoNotification('선택 된 시간표가 없습니다.');
      return
    }

    const {
      TEACHER
    } = CALENDAR_TAB;

    const {
      MONDAY,
      TUESDAY,
      WEDNESDAY,
      THURSDAY,
      FRIDAY
    } = DAY_OF_THE_WEEK


    if (standard === TEACHER) {
      const res = schedules.map(({member, classes}: any) => {
        const total = classes.reduce((acc: any, cur: any) => acc + (cur.classWeekdays?.length ?? 0), 0);

        const studentsTotal = classes.reduce((acc: any, curr: any) => {
          return acc + calculateFilteredStudents(curr.classStudents).length;
        }, 0);


        const classesByDay = Object.keys(DAY_OF_THE_WEEK).reduce((acc, key, index) => {
          acc[key] = classes
            .map((_class: any) => {
              const classWeek = _class.classWeekdays?.find(
                (_week: any) => DAY_OF_THE_WEEK[key] === _week.lessonWeekday
              );
              if (classWeek) {
                const filteredStudents = calculateFilteredStudents(_class.classStudents);
                return `${_class.className} (${filteredStudents.length})`;
              }
              return null;
            })
            .filter(Boolean);
          return acc;
        }, {} as Record<string, string[]>);

        return {
          memberName: member.memberName,
          total,
          students: studentsTotal,
          ...Object.keys(DAY_OF_THE_WEEK).reduce((acc, key) => {
            acc[DAY_OF_THE_WEEK[key]] = classesByDay[key]; // 요일 키로 매핑
            return acc;
          }, {} as Record<string, string[]>),
        };
      })

      exportTitleTable(
        [
          {
            title: '강사',
            dataIndex: 'memberName',
            key: 'memberName'
          },
          {
            title: '총 횟수',
            dataIndex: 'total'
          },
          {
            title: '총 학생수',
            dataIndex: 'students'
          },
          {
            title: '월',
            dataIndex: MONDAY,
          },
          {
            title: '화',
            dataIndex: TUESDAY,
          },
          {
            title: '수',
            dataIndex: WEDNESDAY
          },
          {
            title: '목',
            dataIndex: THURSDAY,
          },
          {
            title: '금',
            dataIndex: FRIDAY
          },
        ],
        res,
        '수강반 캘린더(강사 기준)_' + moment().format('YYYYMMDD'),
        '강사 기준',
        [25, 10, 10, 30, 30, 30, 30, 30],
        '수강반 캘린더 (강사 기준)',
        []
      )
      return;
    }

    let timeRes : any[] = [];
    let timeRes2 : iExcelDataByTime[] = [];
    const timeColumns = [
      {
        title: '시간',
        dataIndex: 'time'
      },
      {
        title: '월',
        dataIndex: MONDAY,
      },
      {
        title: '화',
        dataIndex: TUESDAY,
      },
      {
        title: '수',
        dataIndex: WEDNESDAY
      },
      {
        title: '목',
        dataIndex: THURSDAY,
      },
      {
        title: '금',
        dataIndex: FRIDAY
      },
    ]

    let maxLength = 0;
    Object.keys(schedules).sort().map((time: string) => {
      timeRes = [];
      maxLength = 0;
      Object.keys(schedules[time]).forEach((week: string) => {
        maxLength = Math.max(maxLength, schedules[time][week].length)
      });

      Array(maxLength).fill(1).map((v: number, index: number) => {
        let content = '';
        const timeRes3 = new Map<string, string>()
        Object.keys(DAY_OF_THE_WEEK).map((week: string) => {
          const classInfo = (schedules[time][DAY_OF_THE_WEEK[week]] ?? [])?.at(index);
          if(classInfo) {
            content = classInfo.className;
            if(classInfo.teachers.length > 0) {
              content += '\n' + t('class.class.column24') + ' : ' + classInfo.teachers.at(0).classRoomName;
              content += '\n' + t('statistics.refund.column2') + ' : ' + classInfo.studentsCount;
              classInfo.teachers.map((detail: any) => {
                const { classTimeSeq, memberName, subjectName } = detail;
                content += '\n[' + classTimeSeq + ']' + subjectName + '–' + memberName;
              })
            }
            timeRes3.set(DAY_OF_THE_WEEK[week], content);
          }
        });
        timeRes.push({time:time, mo:timeRes3.get('MO'), tu:timeRes3.get('TU'), we:timeRes3.get('WE'), th:timeRes3.get('TH'), fr:timeRes3.get('FR')});
      })
      timeRes.map((item, index) => {
        if(index === 0) {
          timeRes2.push({time:item.time, MO:item.mo, TU:item.tu, WE:item.we, TH:item.th, FR:item.fr, mergeColumn:['time'], mergeSize:maxLength});
        } else {
          timeRes2.push({time:item.time, MO:item.mo, TU: item.tu, WE:item.we, TH:item.th, FR:item.fr, mergeColumn:[], mergeSize:0});
        }
      })
    })
    exportTitleTable(timeColumns, timeRes2, '수강반 캘린더(시간 기준)_' +  + moment().format('YYYYMMDD'), '시간 기준', [10,20,20,20,20,20], '수강반 캘린더 (시간 기준)',[] );

  }, [schedules, standard, infoNotification, calculateFilteredStudents]);

  useEffect(() => {
    if (semesterCode) return;
    dispatch(doGetSemesters());
  }, []);

  return (
    <>
    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('common.menu.campus')}</label>
            <div>
              <SelectCampus />
            </div>
          </li>
          <li>
            <label>{t('statistics.refund.placeholder1')}</label> {/* 연도 */}
            <Select
              placeholder={t('statistics.refund.placeholder1')}
              value={year}
              onChange={value => setYear(value)}
            >
              {getReverseYears(SY0005).map(item => (
                <Select.Option
                  key={item.detailCode}
                  value={item.detailCode}
                >
                  {item.korName}
                </Select.Option>
              ))}
            </Select>            
          </li>
            <li>
              <label>
                {t('program.semester.column11')}
              </label>
              <Select
                className="w-120p"
                value={semesterType}
                onChange={value => setSemesterType(value)}
              >
                {
                  PR0002?.map(({korName: label, detailCode: value}) => {
                    return (
                      <Select.Option key={value} value={value}>
                        {label}
                      </Select.Option>
                    )
                  })
                }
              </Select>
            </li>  
          <li>
            <label>{t('class.class.placeholder4')}</label>
            <div>
              <Select
                placeholder={t('class.class.placeholder4')}
                value={semesterCode}
                onChange={(value: number) => {
                  dispatch(setCurrentSemesterCode(value));
                  setSemesterCode(value);
                }}
              >
                {semesterData?.map((item: ISemester) => {
                  return (
                    <Option
                      key={item.semesterCode}
                      value={item.semesterCode || ''}
                    >
                      {item.semesterName}
                    </Option>
                  );
                })}
              </Select>
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={search}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>
    <div className="content scrollbar">
      <SimpleHeader
        parentMenu={'수강반'}
        currentMenu={'수강반 캘린더'}
        onlyTitle={true}
      />
      <div>
        <div className="flex transition">
          <div className="col-3">
            <div className="comm-bx__between">
              <h3 className="sub-title">교육단계 선택</h3>
            </div>
            <div className="comm-table__area">
              {
                (classes && Object.keys(classes).length) ? (
                  <div className='border-bottom-light pt-2 pb-2'>
                    <Row style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                      <Col>
                        <Checkbox indeterminate={indeterminate} onChange={onCheckAllChange}
                                  checked={checkAll}>전체</Checkbox>
                      </Col>
                      <Checkbox.Group style={{ display: 'flex', flex: 1, width: '100%' }} value={checkedList}
                                      onChange={onCheckChange}>
                        {
                          educationStage?.map(item => {
                            return (
                              <Col>
                                <Checkbox value={item.detailCode}>{item.korName}</Checkbox>
                              </Col>
                            );
                          })
                        }
                      </Checkbox.Group>
                    </Row>
                  </div>
                ) :  <TableEmpty text={'캠퍼스와 학기를 먼저 검색하세요.'} />
              }
            </div>
            <div className="comm-bx__between">
              <h3 className="sub-title">과정 선택</h3>
            </div>
            <div className="comm-table__area">
              <div className='border-bottom-light pt-2 pb-2'>
                <div>
                  {
                    checkedList && checkedList.length > 0 ? (
                      <>
                        <Col>
                          <Checkbox indeterminate={classesIndeterminate} onChange={onClassCheckAllChange}
                                    checked={classCheckAll}>전체</Checkbox>
                        </Col>
                        <Checkbox.Group style={{ width: '100%' }} value={classCheckedList}
                                        onChange={onClassCheckChange}>
                          {
                            Object.keys(filteredClasses)?.map(key => {
                              return (
                                <Row>
                                  <Divider style={{ fontWeight: 600, borderTopColor: `rgba(0, 0, 0, 0.5)` }} dashed>{key}</Divider>
                                  {
                                    filteredClasses[key]?.map((chunk: any) => {
                                      return chunk?.map((_class: IClass) => {
                                        return (
                                          <Col span={8}>
                                            <Checkbox value={_class.classCode}>{_class.className}</Checkbox>
                                          </Col>
                                        );
                                      });
                                    })
                                  }
                                </Row>
                              );
                            })
                          }
                        </Checkbox.Group>
                      </>
                    ) : <TableEmpty text={'교육 단계를 선택해주세요.'} />
                  }
                </div>
              </div>
            </div>
            <div className="comm-bx__between">
              <h3 className="sub-title">강사 선택</h3>
            </div>
            <div className="comm-table__area">
              <div className='border-bottom-light pt-2 pb-2'>
                <div>
                  {
                    (
                      teachersResponse?.data && teachersResponse?.data.length > 0 &&
                      checkedList && checkedList.length > 0
                    ) ? (
                      <>
                        <Col>
                          <Checkbox indeterminate={teacherIndeterminate} onChange={onTeacherCheckAllChange}
                                    checked={teacherCheckAll}>전체</Checkbox>
                        </Col>
                        <Checkbox.Group style={{ width: '100%' }} value={teacherCheckedList}
                                        onChange={onTeacherCheckChange}>
                          <Row>
                            {
                              teachersResponse?.data?.map((item: any) => {
                                return (
                                  <Col span={8} key={item.memberCode}>
                                    <Checkbox value={item.memberCode}>{item.memberName}({item.weekCount})</Checkbox>
                                  </Col>
                                );
                              })
                            }
                          </Row>
                        </Checkbox.Group>
                      </>
                    ) : <TableEmpty text={'과정을 선택해주세요.'} />
                  }
                </div>
              </div>
            </div>
          </div>
          {
            !isSchedulesEmpty && (
              <div className="transition col-9">
                <div>
                  <div>
                    <div className="comm-tab__line">
                      <ul
                        className=""
                        id="custom-tabs-tab"
                        role="tablist"
                        onClick={onTabClick}
                      >
                        <li className={`${standard === CALENDAR_TAB.TEACHER && 'active'}`}>
                          <a className={"btn-item"} tabIndex={CALENDAR_TAB.TEACHER}>강사 기준</a>
                        </li>
                        <li className={`${standard === CALENDAR_TAB.TIME && 'active'}`}>
                          <a className={"btn-item"} tabIndex={CALENDAR_TAB.TIME}>시간 기준</a>
                        </li>
                      </ul>
                    </div>
                    <div>
                      {
                        schedules && (
                          <div className="tab-content">
                            <div className='comm-bx__space pt-0'>
                              <div className="comm-btn__area justify-content-between">
                                <div>
                                  <Button className="btn-basic green"
                                          onClick={handleExcelDownload} text={`Download`}
                                          useExcelIcon
                                  />
                                </div>
                                <div>
                                  {
                                    standard === CALENDAR_TAB.TIME && (
                                      <Select
                                        className="w-100p"
                                        placeholder={t('class.class.placeholder4')}
                                        value={timeValue}
                                        onChange={(value: string) => setTimeValue(value)}
                                      >
                                        <Option key="all" value={''}>전체</Option>
                                        {timeOptions?.map((item: string) => {
                                          return (
                                            <Option
                                              key={item}
                                              value={item || ''}
                                            >
                                              {item}
                                            </Option>

                                          );
                                        })}
                                      </Select>
                                    )
                                  }
                                </div>
                              </div>
                            </div>
                            {standard === CALENDAR_TAB.TEACHER && (
                              <table className="tb-calendar">
                                <colgroup>
                                  <col style={{ width: '10%' }} />
                                  <col style={{ width: '5%' }} />
                                  <col style={{ width: '5%' }} />
                                  <col style={{ width: '16%' }} />
                                  <col style={{ width: '16%' }} />
                                  <col style={{ width: '16%' }} />
                                  <col style={{ width: '16%' }} />
                                  <col style={{ width: '16%' }} />
                                </colgroup>
                                <thead>
                                <tr>
                                  <th>강사</th>
                                  <th style={{whiteSpace: 'nowrap'}}>총 횟수</th>
                                  <th style={{whiteSpace: 'nowrap'}}>총 학생수</th>
                                  <th>월</th>
                                  <th>화</th>
                                  <th>수</th>
                                  <th>목</th>
                                  <th>금</th>
                                </tr>
                                </thead>
                                <TeachersTbody>
                                  {
                                    schedules && !isFetching &&
                                    schedules?.map((schedule: any) => {
                                      return (
                                        <tr>
                                          <th>{schedule.member.memberName ? schedule.member.memberName : schedule.member.memberEngName}</th>
                                          <td>
                                            {
                                              schedule.classes.reduce((acc: any, cur: any) => {
                                                return acc + cur.classWeekdays?.length;
                                              }, 0)
                                            }
                                          </td>
                                          <td>
                                            {
                                              schedule.classes.reduce((acc: any, curr: any) => {
                                                const filteredClassStudents = curr.classStudents
                                                  ?.filter((student: any) => Object.values(CLASS_STUDENT_STATUS)
                                                    .includes(student.classStudentStatusCode))

                                                return acc + filteredClassStudents?.length;
                                              }, 0)
                                            }
                                          </td>
                                          {
                                            Object.keys(DAY_OF_THE_WEEK)
                                              .map((key: string) => {
                                                return (
                                                  <td style={{
                                                    backgroundColor: isTeacherNoSchedule(schedule, DAY_OF_THE_WEEK[key]) ? '#fff' : '#ccc',
                                                  }}>
                                                    {
                                                      schedule.classes.map((_class: any) => {
                                                        const classWeek = _class.classWeekdays?.find((_week: any) => DAY_OF_THE_WEEK[key] === _week.lessonWeekday);
                                                        const filteredClassStudents = _class?.classStudents
                                                          ?.filter((student: any) => Object.values(CLASS_STUDENT_STATUS)
                                                            .includes(student.classStudentStatusCode))

                                                        if (classWeek) {
                                                          return (
                                                            <p>{`${_class.className} (${filteredClassStudents.length ?? 0})`}</p>
                                                          );
                                                        }
                                                      })
                                                    }
                                                  </td>
                                                );
                                              })
                                          }
                                        </tr>
                                      )
                                    })
                                  }

                                </TeachersTbody>
                              </table>
                            )}
                            {standard === CALENDAR_TAB.TIME && (
                              <>
                                <table className="tb-calendar">
                                  <colgroup>
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '18%' }} />
                                    <col style={{ width: '18%' }} />
                                    <col style={{ width: '18%' }} />
                                    <col style={{ width: '18%' }} />
                                    <col style={{ width: '18%' }} />
                                  </colgroup>
                                  <thead>
                                  <tr>
                                    <th>시간</th>
                                    <th>월</th>
                                    <th>화</th>
                                    <th>수</th>
                                    <th>목</th>
                                    <th>금</th>
                                  </tr>
                                  </thead>
                                  <tbody>
                                  {
                                    schedules && !isFetching &&
                                    Object.keys(schedules).sort().map((time: string) => {
                                      let maxLength = 0;
                                      Object.keys(schedules[time]).forEach((week: string) => {
                                        maxLength = Math.max(maxLength, schedules[time][week].length)
                                      })

                                      if (isNaN(maxLength)) return;

                                      return Array(maxLength).fill(1).map((v: number, index: number) => {
                                        return (
                                          <tr>
                                            {index == 0 && <th rowSpan={maxLength}>{time}</th>}
                                            {
                                              Object.keys(DAY_OF_THE_WEEK).map((week: string) => {
                                                const classInfo = (schedules[time][DAY_OF_THE_WEEK[week]] ?? [])?.at(index);

                                                if (!classInfo) return <td style={{ backgroundColor: '#ccc' }}></td>;
                                                const { className, studentsCount, teachers, students } = classInfo;

                                                return (
                                                  <td style={{
                                                    position: "relative"
                                                  }}>
                                                    {
                                                      <>
                                                        <p className="tit">
                                                          <span>{className}</span>
                                                        </p>
                                                        {
                                                          teachers.length ? (
                                                            <>
                                                              <p className="tit">
                                                                <i className="cn"></i>
                                                                <span>{teachers.at(0).classRoomName}</span>
                                                              </p>
                                                              <StudentPopover
                                                                students={students}
                                                                studentsCount={studentsCount}
                                                              />
                                                            </>
                                                          ) : <></>
                                                        }
                                                        {
                                                          teachers.map((detail: any) => {
                                                            const { classTimeSeq, memberName, subjectName } = detail;
                                                            return <p>[{classTimeSeq}] {subjectName} – {memberName} </p>;
                                                          })
                                                        }
                                                      </>
                                                    }
                                                  </td>
                                                )
                                              })
                                            }
                                          </tr>
                                        )
                                      })
                                    })
                                  }
                                  </tbody>
                                </table>
                              </>
                            )}
                          </div>
                        )
                      }
                    </div>
                  </div>
                </div>
              </div>
            )
          }
        </div>
      </div>
    </div>
    </>
  )
}

export default ClassCalendar;